// ═══════════════════════════════════════
// ATLAS BEASTS — SHOP
// Fortnite-style item shop with featured items
// ═══════════════════════════════════════

import { useState, useEffect } from 'react'

const FEATURED_ITEMS = [
  { id: 'legendary-pack', name: 'Legendary Starter Pack', category: 'special', price: 2500, currency: 'gems', icon: '/assets/kenney/kenney_mini-dungeon/Previews/chest.png', rarity: 'legendary', description: 'Contains 3 legendary items and 10,000 gold', isBundle: true },
  { id: 'xp-boost-bundle', name: 'XP Boost Bundle', category: 'special', price: 1200, currency: 'gems', icon: '/assets/kenney/kenney_mini-arena/Previews/trophy.png', rarity: 'epic', description: '3x XP Boosts (1 hour each)', isBundle: true },
]

const SHOP_ITEMS = [
  { id: 'health-potion', name: 'Health Potion', category: 'consumables', price: 100, currency: 'gold', icon: '/assets/icons/element-fire.png', rarity: 'common', description: 'Restores 50 HP' },
  { id: 'revive', name: 'Revive', category: 'consumables', price: 500, currency: 'gold', icon: '/assets/icons/element-water.png', rarity: 'uncommon', description: 'Revive fainted creature' },
  { id: 'fire-essence', name: 'Fire Essence', category: 'materials', price: 50, currency: 'gold', icon: '/assets/icons/element-fire.png', rarity: 'common', description: 'Fire crafting material' },
  { id: 'water-crystal', name: 'Water Crystal', category: 'materials', price: 50, currency: 'gold', icon: '/assets/icons/element-water.png', rarity: 'uncommon', description: 'Water crafting material' },
  { id: 'steel-blade', name: 'Steel Blade', category: 'equipment', price: 2000, currency: 'gold', icon: '/assets/kenney/kenney_mini-dungeon/Previews/weapon-sword.png', rarity: 'rare', description: '+20 Attack weapon' },
  { id: 'shield-core', name: 'Shield Core', category: 'equipment', price: 1500, currency: 'gold', icon: '/assets/kenney/kenney_mini-dungeon/Previews/shield-round.png', rarity: 'epic', description: '+15 Defense shield' },
  { id: 'mystery-box', name: 'Mystery Box', category: 'special', price: 1000, currency: 'gems', icon: '/assets/kenney/kenney_mini-dungeon/Previews/chest.png', rarity: 'legendary', description: 'Random rare item' },
  { id: 'xp-boost', name: 'XP Boost', category: 'special', price: 500, currency: 'gems', icon: '/assets/kenney/kenney_mini-arena/Previews/trophy.png', rarity: 'epic', description: '2x XP for 1 hour' },
]

const RARITY_COLORS = {
  common: '#8A8AA5',
  uncommon: '#00F0FF',
  rare: '#39FF14',
  epic: '#BF00FF',
  legendary: '#FFE800',
}

const CURRENCY_COLORS = {
  gold: '#FFE800',
  gems: '#BF00FF',
}

export default function Shop() {
  const [selectedItem, setSelectedItem] = useState<typeof FEATURED_ITEMS[0] | typeof SHOP_ITEMS[0] | null>(null)
  const [playerGold, setPlayerGold] = useState(5000)
  const [playerGems, setPlayerGems] = useState(100)
  const [rotationEnd, setRotationEnd] = useState('')

  useEffect(() => {
    // Set rotation end time to tomorrow at 12 AM
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(0, 0, 0, 0)
    setRotationEnd(tomorrow.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
  }, [])

  function handlePurchase(item: typeof FEATURED_ITEMS[0] | typeof SHOP_ITEMS[0]) {
    if (item.currency === 'gold' && playerGold >= item.price) {
      setPlayerGold(prev => prev - item.price)
    } else if (item.currency === 'gems' && playerGems >= item.price) {
      setPlayerGems(prev => prev - item.price)
    }
    setSelectedItem(null)
  }

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: '100vh', background: '#020510' }}>

      {/* Top Bar - Fortnite Style */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(58,58,90,0.5)', background: 'linear-gradient(135deg, rgba(0,240,255,0.05), rgba(191,0,255,0.05))' }}>
        <div>
          <h1 className="font-black text-2xl uppercase tracking-wider" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>ITEM SHOP</h1>
          <p className="text-xs text-[#8A8AA5]" style={{ fontFamily: 'Space Mono, monospace' }}>Rotates in: {rotationEnd}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(255,232,0,0.1)', border: '1px solid rgba(255,232,0,0.3)' }}>
            <div className="w-5 h-5 rounded" style={{ background: '#FFE800' }} />
            <span className="font-bold text-sm" style={{ color: '#FFE800', fontFamily: 'Space Mono, monospace' }}>
              {playerGold.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(191,0,255,0.1)', border: '1px solid rgba(191,0,255,0.3)' }}>
            <div className="w-5 h-5 rounded" style={{ background: '#BF00FF' }} />
            <span className="font-bold text-sm" style={{ color: '#BF00FF', fontFamily: 'Space Mono, monospace' }}>
              {playerGems}
            </span>
          </div>
        </div>
      </div>

      {/* Featured Items - Large Cards */}
      <div className="p-4">
        <h2 className="font-bold text-sm text-[#FFE800] mb-3 uppercase tracking-wider" style={{ fontFamily: 'Space Mono, monospace' }}>⭐ Featured</h2>
        <div className="grid grid-cols-1 gap-3">
          {FEATURED_ITEMS.map(item => (
            <FeaturedCard key={item.id} item={item} playerGold={playerGold} playerGems={playerGems} onSelect={setSelectedItem} />
          ))}
        </div>
      </div>

      {/* Daily Items - Grid */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <h2 className="font-bold text-sm text-[#00F0FF] mb-3 uppercase tracking-wider" style={{ fontFamily: 'Space Mono, monospace' }}>📦 Daily Items</h2>
        <div className="grid grid-cols-2 gap-3">
          {SHOP_ITEMS.map(item => (
            <ShopItemCard key={item.id} item={item} playerGold={playerGold} playerGems={playerGems} onSelect={setSelectedItem} />
          ))}
        </div>
      </div>

      {/* Item Detail Modal */}
      {selectedItem && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(2,5,16,0.95)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-2xl border p-6 max-w-sm w-full" style={{ background: 'rgba(13,13,21,0.98)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <button
              onClick={() => setSelectedItem(null)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-[#8A8AA5] hover:text-[#EAF6FF]"
              style={{ border: '1px solid rgba(58,58,90,0.5)' }}
            >
              ✕
            </button>
            
            <div className="text-center mb-4">
              <div className="relative inline-block mb-3">
                <img src={selectedItem.icon} alt={selectedItem.name} className="w-24 h-24 object-contain mx-auto" />
                {'rarity' in selectedItem && (
                  <span 
                    className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase"
                    style={{ 
                      color: RARITY_COLORS[selectedItem.rarity as keyof typeof RARITY_COLORS],
                      background: `${RARITY_COLORS[selectedItem.rarity as keyof typeof RARITY_COLORS]}20`,
                      border: `1px solid ${RARITY_COLORS[selectedItem.rarity as keyof typeof RARITY_COLORS]}50`,
                      fontFamily: 'Space Mono, monospace',
                    }}
                  >
                    {selectedItem.rarity}
                  </span>
                )}
              </div>
              <h2 className="font-bold text-2xl text-[#EAF6FF] uppercase" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                {selectedItem.name}
              </h2>
              <p className="text-sm text-[#8A8AA5] mt-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                {selectedItem.description}
              </p>
            </div>

            <div className="space-y-3 mb-4">
              <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(58,58,90,0.3)' }}>
                <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>Price</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded" style={{ background: CURRENCY_COLORS[selectedItem.currency as keyof typeof CURRENCY_COLORS] }} />
                  <span className="font-bold text-xl" style={{ 
                    color: CURRENCY_COLORS[selectedItem.currency as keyof typeof CURRENCY_COLORS],
                    fontFamily: 'Rajdhani, system-ui',
                  }}>
                    {selectedItem.price.toLocaleString()}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(58,58,90,0.3)' }}>
                <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>Your Balance</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded" style={{ background: CURRENCY_COLORS[selectedItem.currency as keyof typeof CURRENCY_COLORS] }} />
                  <span className="font-bold text-sm text-[#EAF6FF]" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                    {selectedItem.currency === 'gold' ? playerGold.toLocaleString() : playerGems}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={() => handlePurchase(selectedItem)}
              disabled={
                (selectedItem.currency === 'gold' && playerGold < selectedItem.price) ||
                (selectedItem.currency === 'gems' && playerGems < selectedItem.price)
              }
              className="w-full py-4 rounded-lg font-black uppercase tracking-wider text-lg transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                background: 'linear-gradient(135deg, #00F0FF 0%, #00F0FF80 100%)',
                color: '#020510',
                fontFamily: 'Rajdhani, system-ui',
              }}
            >
              Purchase
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function FeaturedCard({ item, playerGold, playerGems, onSelect }: { 
  item: typeof FEATURED_ITEMS[0]; 
  playerGold: number; 
  playerGems: number; 
  onSelect: (item: typeof FEATURED_ITEMS[0]) => void 
}) {
  const canAfford = item.currency === 'gold' ? playerGold >= item.price : playerGems >= item.price
  const currencyColor = CURRENCY_COLORS[item.currency as keyof typeof CURRENCY_COLORS]
  const rarityColor = RARITY_COLORS[item.rarity as keyof typeof RARITY_COLORS]

  return (
    <button
      onClick={() => onSelect(item)}
      disabled={!canAfford}
      className={`relative rounded-2xl border p-5 transition-all active:scale-98 hover:scale-[1.02] text-left overflow-hidden ${
        !canAfford ? 'opacity-50 cursor-not-allowed' : ''
      }`}
      style={{ 
        background: `linear-gradient(135deg, ${rarityColor}10, rgba(13,13,21,0.95))`, 
        borderColor: canAfford ? `${rarityColor}50` : 'rgba(58,58,90,0.5)',
        boxShadow: canAfford ? `0 0 20px ${rarityColor}20` : 'none',
      }}
    >
      {/* Rarity glow effect */}
      <div className="absolute inset-0 opacity-20" style={{ 
        background: `radial-gradient(circle at top right, ${rarityColor}, transparent 70%)`,
      }} />
      
      <div className="relative flex items-center gap-4">
        <div className="relative">
          <img src={item.icon} alt={item.name} className="w-16 h-16 object-contain" />
          <span 
            className="absolute -top-1 -right-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase"
            style={{ 
              color: rarityColor,
              background: `${rarityColor}20`,
              border: `1px solid ${rarityColor}50`,
              fontFamily: 'Space Mono, monospace',
            }}
          >
            {item.rarity}
          </span>
        </div>
        <div className="flex-1">
          <p className="font-black text-lg text-[#EAF6FF] uppercase leading-tight mb-1" style={{ fontFamily: 'Rajdhani, system-ui' }}>
            {item.name}
          </p>
          <p className="text-xs text-[#8A8AA5] mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
            {item.description}
          </p>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded" style={{ background: currencyColor }} />
            <span className="font-black text-lg" style={{ 
              color: currencyColor,
              fontFamily: 'Rajdhani, system-ui',
            }}>
              {item.price.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </button>
  )
}

function ShopItemCard({ item, playerGold, playerGems, onSelect }: { 
  item: typeof SHOP_ITEMS[0]; 
  playerGold: number; 
  playerGems: number; 
  onSelect: (item: typeof SHOP_ITEMS[0]) => void 
}) {
  const canAfford = item.currency === 'gold' ? playerGold >= item.price : playerGems >= item.price
  const currencyColor = CURRENCY_COLORS[item.currency as keyof typeof CURRENCY_COLORS]
  const rarityColor = RARITY_COLORS[item.rarity as keyof typeof RARITY_COLORS]

  return (
    <button
      onClick={() => onSelect(item)}
      disabled={!canAfford}
      className={`relative rounded-xl border p-4 transition-all active:scale-95 hover:scale-105 text-left overflow-hidden ${
        !canAfford ? 'opacity-50 cursor-not-allowed' : ''
      }`}
      style={{ 
        background: 'rgba(13,13,21,0.95)', 
        borderColor: canAfford ? `${rarityColor}40` : 'rgba(58,58,90,0.5)',
        boxShadow: canAfford ? `0 0 12px ${rarityColor}15` : 'none',
      }}
    >
      {/* Rarity indicator */}
      <div className="absolute top-2 right-2">
        <span 
          className="px-2 py-0.5 rounded-full text-[8px] font-bold uppercase"
          style={{ 
            color: rarityColor,
            background: `${rarityColor}20`,
            border: `1px solid ${rarityColor}40`,
            fontFamily: 'Space Mono, monospace',
          }}
        >
          {item.rarity}
        </span>
      </div>
      
      <img src={item.icon} alt={item.name} className="w-12 h-12 object-contain mb-2" />
      <p className="font-bold text-sm text-[#EAF6FF] uppercase leading-tight mb-1 pr-8" style={{ fontFamily: 'Rajdhani, system-ui' }}>
        {item.name}
      </p>
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded" style={{ background: currencyColor }} />
          <span className="font-bold text-xs" style={{ 
            color: currencyColor,
            fontFamily: 'Space Mono, monospace',
          }}>
            {item.price}
          </span>
        </div>
        {!canAfford && (
          <span className="text-[9px] text-[#FF4800] font-bold uppercase" style={{ fontFamily: 'Space Mono, monospace' }}>
            Need {item.price - (item.currency === 'gold' ? playerGold : playerGems)}
          </span>
        )}
      </div>
    </button>
  )
}
